import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridTable } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import { FiEye, FiUsers, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { fetchDocViewStats, fetchDocViewLogs } from 'redux/documentation/docViewSlice';
const toISO = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
const PRESETS = [{ label: '7d', days: 7 }, { label: '30d', days: 30 }, { label: '90d', days: 90 }];

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtShort = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white">
    <div className={`tw-w-10 tw-h-10 tw-rounded tw-flex tw-items-center tw-justify-center ${tone}`}>
      <Icon size={18} />
    </div>
    <div>
      <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 tw-mb-0">{label}</p>
      <p className="tw-text-xl tw-font-bold tw-text-slate-800 tw-mb-0">{value}</p>
    </div>
  </div>
);
StatCard.propTypes = { icon: PropTypes.elementType, label: PropTypes.string, value: PropTypes.node, tone: PropTypes.string };

// Admin-only per-user, per-day view statistics for a documentation type.
const DocViewStatsPanel = ({ docType, title = 'View statistics' }) => {
  const dispatch = useDispatch();
  const { stats } = useSelector((s) => s.doc_view);
  const [startDate, setStartDate] = useState(toISO(daysAgo(30)));
  const [endDate, setEndDate]     = useState(toISO(new Date()));

  useEffect(() => {
    dispatch(fetchDocViewStats({ docType, startDate, endDate }));
  }, [dispatch, docType, startDate, endDate]);

  const applyPreset = (days) => { setStartDate(toISO(daysAgo(days))); setEndDate(toISO(new Date())); };

  const summary = stats?.summary;
  const byDay   = useMemo(() => summary?.by_day || [], [summary]);
  const byDoc   = useMemo(() => summary?.by_doc || [], [summary]);

  // ── Server-side detail grid (per user · entry · day) ──
  const [logRows, setLogRows]       = useState([]);
  const [logTotal, setLogTotal]     = useState(0);
  const [logLoading, setLogLoading] = useState(false);

  const handleServerFetch = useCallback(async ({ page, pageSize, sortBy, sortDir, filters }) => {
    setLogLoading(true);
    try {
      const res = await dispatch(fetchDocViewLogs({
        docType, startDate, endDate,
        startRow: (page - 1) * pageSize,
        endRow: page * pageSize,
        sortModel: sortBy ? [{ colId: sortBy, sort: sortDir }] : [],
        filterModel: filters || {},
      })).unwrap();
      setLogRows(res.rows || []);
      setLogTotal(res.lastRow || 0);
    } catch (e) {
      setLogRows([]);
      setLogTotal(0);
    } finally {
      setLogLoading(false);
    }
  }, [dispatch, docType, startDate, endDate]);

  // AgGridTable only fetches on user-driven sort/filter/page changes — it does not
  // fire an initial load. Drive the first page from here, and refetch whenever the
  // doc type or date range changes (handleServerFetch is memoized on those).
  useEffect(() => {
    handleServerFetch({ page: 1, pageSize: 20, sortBy: null, sortDir: 'desc', filters: {} });
  }, [handleServerFetch]);

  const columnDefs = useMemo(() => [
    { headerName: 'Date', field: 'date', colId: 'viewed_on', width: 150,
      filter: 'agDateColumnFilter', valueFormatter: (p) => fmtDate(p.value) },
    { headerName: 'User', field: 'user_name', colId: 'user.name', flex: 1, minWidth: 140,
      filter: 'agTextColumnFilter',
      valueGetter: (p) => p.data?.user_name || (p.data?.user_id ? `#${p.data.user_id}` : '—') },
    { headerName: 'Entry', field: 'doc_title', colId: 'doc_title', flex: 2, minWidth: 200,
      filter: 'agTextColumnFilter',
      valueGetter: (p) => p.data?.doc_title || p.data?.doc_key || '—' },
    { headerName: 'Views', field: 'view_count', colId: 'view_count', width: 120,
      type: 'rightAligned', filter: 'agNumberColumnFilter' },
  ], []);

  // Views + unique users per day (bar + line).
  const dayChartOption = useMemo(() => ({
    grid: { top: 24, right: 16, bottom: 44, left: 36 },
    tooltip: { trigger: 'axis' },
    legend: { data: ['Views', 'Unique users'], bottom: 0, itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11, color: '#64748b' } },
    xAxis: {
      type: 'category',
      data: byDay.map((d) => fmtShort(d.date)),
      axisLabel: { fontSize: 10, color: '#64748b', hideOverlap: true },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
    },
    yAxis: {
      type: 'value', minInterval: 1,
      axisLabel: { fontSize: 10, color: '#94a3b8' },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: [
      { name: 'Views', type: 'bar', data: byDay.map((d) => d.views), barMaxWidth: 26, itemStyle: { color: '#3b82f6', borderRadius: [3, 3, 0, 0] } },
      { name: 'Unique users', type: 'line', data: byDay.map((d) => d.users), smooth: true, symbolSize: 6, itemStyle: { color: '#10b981' }, lineStyle: { color: '#10b981', width: 2 } },
    ],
  }), [byDay]);

  // Top entries by views (horizontal bar).
  const topDocs = useMemo(() => byDoc.slice(0, 8), [byDoc]);
  const docChartOption = useMemo(() => ({
    grid: { top: 8, right: 24, bottom: 8, left: 8, containLabel: true },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: { type: 'value', minInterval: 1, axisLabel: { fontSize: 10, color: '#94a3b8' }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    yAxis: {
      type: 'category',
      data: topDocs.map((d) => d.doc_title || d.doc_key).reverse(),
      axisLabel: { fontSize: 10, color: '#475569', width: 150, overflow: 'truncate' },
      axisTick: { show: false }, axisLine: { show: false },
    },
    series: [{ name: 'Views', type: 'bar', data: topDocs.map((d) => d.views).reverse(), barMaxWidth: 16, itemStyle: { color: '#8b5cf6', borderRadius: [0, 3, 3, 0] } }],
  }), [topDocs]);

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
      {/* Header + filters */}
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-slate-100 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-[180px]">
          <FiEye size={16} className="tw-text-blue-600" />
          <p className="tw-text-sm tw-font-bold tw-text-slate-700 tw-mb-0">{title}</p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1">
          {PRESETS.map((p) => (
            <button key={p.label} type="button" onClick={() => applyPreset(p.days)}
              className="tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer">
              {p.label}
            </button>
          ))}
        </div>
        <input type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)}
          className="tw-px-2 tw-py-1 tw-text-xs tw-rounded-md tw-border tw-border-slate-200" />
        <span className="tw-text-slate-400 tw-text-xs">→</span>
        <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)}
          className="tw-px-2 tw-py-1 tw-text-xs tw-rounded-md tw-border tw-border-slate-200" />
        <button type="button" onClick={() => dispatch(fetchDocViewStats({ docType, startDate, endDate }))}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer">
          <FiRefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-p-4">
        <StatCard icon={FiEye}      label="Total views"    value={summary?.total_views ?? 0}  tone="tw-bg-blue-50 tw-text-blue-600" />
        <StatCard icon={FiUsers}    label="Unique users"   value={summary?.unique_users ?? 0} tone="tw-bg-emerald-50 tw-text-emerald-600" />
        <StatCard icon={FiFileText} label="Entries viewed" value={byDoc.length}               tone="tw-bg-violet-50 tw-text-violet-600" />
      </div>

      {/* Charts */}
      {(byDay.length > 0 || topDocs.length > 0) && (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[1.6fr_1fr] tw-gap-4 tw-px-4 tw-pb-2">
          <div className="tw-border tw-border-slate-200 tw-rounded-lg tw-p-3">
            <p className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">Views per day</p>
            <ReactECharts option={dayChartOption} style={{ height: 230 }} opts={{ renderer: 'canvas' }} notMerge lazyUpdate />
          </div>
          <div className="tw-border tw-border-slate-200 tw-rounded-lg tw-p-3">
            <p className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">Top entries</p>
            <ReactECharts option={docChartOption} style={{ height: 230 }} opts={{ renderer: 'canvas' }} notMerge lazyUpdate />
          </div>
        </div>
      )}

      {/* Detail table: per user, per day, per entry — server-side (pagination/sort/filter) */}
      <div className="tw-px-4 tw-pb-4">
        <AgGridTable
          serverSide
          onFetch={handleServerFetch}
          rowData={logRows}
          totalRecords={logTotal}
          loading={logLoading}
          columnDefs={columnDefs}
          pageSize={20}
          height="52vh"
        />
      </div>
    </div>
  );
};

DocViewStatsPanel.propTypes = {
  docType: PropTypes.oneOf(['changelog', 'user_guide']).isRequired,
  title: PropTypes.string,
};

export default DocViewStatsPanel;
